import { IN_STOCK, type RingSizeData } from '@jouzen/ecom-components';
import { useEffect, useReducer } from 'react';

import type { CurrentRouteValues } from '@/app/[locale]/product/_components/HardwarePDP/_hooks/useCurrentRoute';
import type { Order } from '@/app/[locale]/product/_components/HardwarePDP/_types/order';
import { type SELECT_SIZE, SIZING_KIT } from '@/app/[locale]/product/_consts';
import { OR4_PDP_SESSION_STORAGE_KEY } from '@/app/[locale]/product/_consts/sessionStorage';
import MembershipSelection from '@/app/[locale]/product/_steps/enums/MembershipSelection';
import WarrantySelection from '@/app/[locale]/product/_steps/enums/WarrantySelection';
import type { ListProduct, Variant } from '@/queries/Products';
import { readFromSessionStorage } from '@/utils/sessionStorage';

import {
  CURRENT_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  INITIAL_STATE,
  ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  ONE_YEAR_MEMBERSHIP_SELECTED_SUMMARY_KEY,
  ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
  reducer,
  SIZE_SELECTED_SUMMARY_KEY,
  SIZING_KIT_SUMMARY_KEY,
  THREE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
  TWO_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
} from './useOR4PDPReducer';

export interface ParsedState {
  order: Order;
}

export type RadioValue =
  | typeof SELECT_SIZE
  | typeof SIZING_KIT
  | MembershipSelection
  | WarrantySelection;

interface UseBuildOrderProps {
  currentRoute: CurrentRouteValues;
  oneMonthMembershipSku: string;
  oneYearMembershipSku: string;
  productsData: ListProduct[] | undefined;
  ringSizesData: RingSizeData[] | undefined;
  sizeLaterData: Variant | undefined;
  threeYearWarrantySku: string;
  twoYearWarrantySku: string;
}

const useBuildOrder = ({
  currentRoute,
  oneMonthMembershipSku,
  oneYearMembershipSku,
  productsData,
  ringSizesData,
  sizeLaterData,
  threeYearWarrantySku,
  twoYearWarrantySku,
}: UseBuildOrderProps) => {
  const [{ order }, dispatch] = useReducer(reducer, INITIAL_STATE);
  const finish = currentRoute.finish.value;

  useEffect(() => {
    const storedState = readFromSessionStorage(OR4_PDP_SESSION_STORAGE_KEY);
    if (storedState) {
      const parsedState = JSON.parse(storedState) as ParsedState;
      dispatch({ type: 'SET_ORDER', payload: parsedState.order });
    } else if (productsData) {
      dispatch({
        type: 'SET_ORDER',
        payload: {
          finish: {
            value: finish,
          },
          size: {
            value: SIZING_KIT,
            sku: sizeLaterData?.sku ?? '',
            stockStatus: IN_STOCK,
            summaryKey: SIZING_KIT_SUMMARY_KEY,
          },
          membership: {
            value: MembershipSelection.OneMonth,
            sku: oneMonthMembershipSku,
            summaryKey: ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY,
          },
          warranty: {
            value: WarrantySelection.OneYear,
            sku: '',
            summaryKey: ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY,
          },
          ready: true,
        },
      });
    }
  }, [finish, oneMonthMembershipSku, productsData, sizeLaterData?.sku]);

  const handleRadioChange = (value: string): void => {
    const _val = value as RadioValue;
    if (value === SIZING_KIT) {
      dispatch({
        type: 'UPDATE_SIZE',
        payload: {
          value: SIZING_KIT,
          sku: sizeLaterData?.sku ?? '',
          stockStatus: IN_STOCK,
          summaryKey: SIZING_KIT_SUMMARY_KEY,
        },
      });
    } else if (
      Object.values(MembershipSelection).includes(value as MembershipSelection)
    ) {
      let sku = '';
      let summaryKey = ONE_MONTH_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      if (_val === MembershipSelection.OneMonth) {
        sku = oneMonthMembershipSku;
      } else if (_val === MembershipSelection.OneYear) {
        sku = oneYearMembershipSku;
        summaryKey = ONE_YEAR_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      } else if (_val === MembershipSelection.CurrentMember) {
        sku = oneMonthMembershipSku; // current member uses the one month SKU
        summaryKey = CURRENT_MEMBERSHIP_SELECTED_SUMMARY_KEY;
      }
      dispatch({
        type: 'UPDATE_RADIO',
        payload: {
          value,
          sku,
          summaryKey,
          field: 'membership',
        },
      });
    } else if (
      Object.values(WarrantySelection).includes(value as WarrantySelection)
    ) {
      let sku = '';
      let summaryKey = ONE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      if (_val === WarrantySelection.TwoYear) {
        sku = twoYearWarrantySku;
        summaryKey = TWO_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      } else if (_val === WarrantySelection.ThreeYear) {
        sku = threeYearWarrantySku;
        summaryKey = THREE_YEAR_WARRANTY_SELECTED_SUMMARY_KEY;
      }
      dispatch({
        type: 'UPDATE_RADIO',
        payload: {
          value,
          sku,
          summaryKey,
          field: 'warranty',
        },
      });
    }
  };

  const handleSizeChange = (val: string): void => {
    dispatch({
      type: 'UPDATE_SIZE',
      payload: {
        value: val,
        sku: ringSizesData?.find(({ size }) => size === val)?.sku ?? '',
        stockStatus:
          ringSizesData?.find(({ size }) => size === val)?.stockStatus ??
          IN_STOCK,
        summaryKey: SIZE_SELECTED_SUMMARY_KEY,
      },
    });
  };

  return {
    order,
    onRadioChange: handleRadioChange,
    onSizeChange: handleSizeChange,
  };
};

export default useBuildOrder;
